import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  DownloadSimple,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Trash,
  Bank,
  PencilSimple,
  LinkBreak,
  X,
  Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatEUR, formatDate } from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import {
  REMESA_ESTADO_LABEL,
  REMESA_ESTADO_STYLE,
  REMESA_TRANSICIONES,
  isEditable,
} from "@/lib/remesa";
import { CONCEPTO_LABEL, TIPOS } from "@/constants/recibos";

const totalConceptos = (cs = []) =>
  cs.reduce((s, c) => s + (Number(c.importe) || 0), 0);

const aplicaRecargoMesImpar = (remesaFecha, group) => {
  try {
    const mes = Number(String(remesaFecha).split("-")[1]);

    if (mes % 2 !== 1) {
      return false;
    }

    if (!group.mandato_id) {
      return false;
    }

    const yaTieneGastos = group.recibos.some((r) =>
      (r.conceptos || []).some((c) => c.tipo === "gastos"),
    );

    return !yaTieneGastos;
  } catch {
    return false;
  }
};

export default function RemesaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.MANAGE_REMESAS);

  const [remesa, setRemesa] = useState(null);
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [falleros, setFalleros] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/remesas/${id}`);
      setRemesa(r.data);
      setRecibos(r.data.recibos || []);

      const fallRes = await api.get("/falleros");
      setFalleros(fallRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la remesa");
      navigate("/remesas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [id]);

  const groupedRecibos = useMemo(() => {
    const map = {};

    for (const r of recibos) {
      const key = r.mandato_id || r.id;

      if (!map[key]) {
        map[key] = {
          group_key: key,
          mandato_id: r.mandato_id,
          referencia: r.mandato_referencia,
          titular: r.mandato_titular,
          tipo: r.mandato_tipo,
          total: 0,
          recibos: [],
        };
      }

      map[key].total += Number(r.importe || 0);
      map[key].recibos.push(r);
    }

    return Object.values(map).sort((a, b) =>
      String(a.referencia || "").localeCompare(
        String(b.referencia || ""),
        "es",
        {
          numeric: false,
          sensitivity: "base",
        },
      ),
    );
  }, [recibos]);

  if (loading || !remesa) {
    return (
      <div
        className="p-6 md:p-10 text-med-muted"
        data-testid="remesa-detail-loading"
      >
        Cargando remesa…
      </div>
    );
  }

  const estado = remesa.estado || "borrador";
  const editable = isEditable(estado);
  const transicionesPosibles = REMESA_TRANSICIONES[estado] || [];

  const transicionar = async (nuevoEstado) => {
    if (
      !window.confirm(
        `¿Cambiar el estado a "${REMESA_ESTADO_LABEL[nuevoEstado]}"?`,
      )
    )
      return;
    try {
      await api.post(`/remesas/${id}/transicion`, { estado: nuevoEstado });
      toast.success(`Estado actualizado a ${REMESA_ESTADO_LABEL[nuevoEstado]}`);
      fetchAll();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudo cambiar el estado",
      );
    }
  };

  const eliminar = async () => {
    if (
      !window.confirm(
        `¿Eliminar la remesa "${remesa.nombre}"? Los recibos volverán a estar disponibles.`,
      )
    )
      return;
    try {
      await api.delete(`/remesas/${id}`);
      toast.success("Remesa eliminada");
      navigate("/remesas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const descargarSepa = async () => {
    try {
      const token = localStorage.getItem("local_token");
      const res = await fetch(`${API}/remesas/${id}/sepa-pain008`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let detail = "No se pudo generar el SEPA";
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch {
          /* ignore */
        }
        toast.error(detail);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sepa-pain008-${remesa.nombre.replace(/\s+/g, "_")}.xml`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("SEPA descargado");
    } catch {
      toast.error("Error descargando el SEPA");
    }
  };

  const openEdit = (rec) => {
    setEditModal({
      isEdit: true,
      data: {
        ...rec,
        fecha_pago: rec.fecha_pago?.slice(0, 10) || "",
        conceptos: rec.conceptos || [],
      },
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/recibos/${editModal.data.id}`, {
        fallero_id: editModal.data.fallero_id,

        conceptos: editModal.data.conceptos,

        fecha_pago: editModal.data.fecha_pago,

        estado: editModal.data.estado,

        notas: editModal.data.notas || "",

        remesa_id: editModal.data.remesa_id,
      });

      toast.success("Recibo actualizado");

      setEditModal(null);

      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };
  const unlinkRecibo = async (rec) => {
    if (!window.confirm("¿Quitar recibo de la remesa?")) {
      return;
    }

    try {
      await api.put(`/recibos/${rec.id}`, {
        fallero_id: rec.fallero_id,
        conceptos: rec.conceptos,
        fecha_pago: rec.fecha_pago,
        estado: rec.estado,
        notas: rec.notas || "",
        remesa_id: null,
      });

      toast.success("Recibo desvinculado");

      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo desvincular");
    }
  };

  const updateConcepto = (idx, key, value) => {
    const next = [...editModal.data.conceptos];

    next[idx] = {
      ...next[idx],
      [key]: value,
    };

    setEditModal({
      ...editModal,
      data: {
        ...editModal.data,
        conceptos: next,
      },
    });
  };

  const removeConcepto = (idx) => {
    const next = editModal.data.conceptos.filter((_, i) => i !== idx);

    setEditModal({
      ...editModal,
      data: {
        ...editModal.data,
        conceptos: next,
      },
    });
  };

  const addConcepto = () => {
    const usados = new Set(editModal.data.conceptos.map((c) => c.tipo));

    const tipo = TIPOS.find((t) => !usados.has(t)) || "cuota";

    setEditModal({
      ...editModal,
      data: {
        ...editModal.data,
        conceptos: [
          ...editModal.data.conceptos,
          {
            tipo,
            importe: 0,
            descripcion: "",
          },
        ],
      },
    });
  };
  const estadoStyle =
    REMESA_ESTADO_STYLE[estado] || REMESA_ESTADO_STYLE.borrador;

  const descargarExcel = () => {
    window.open(`${api.defaults.baseURL}/remesas/${id}/excel`, "_blank");
  };

  return (
    <div className="p-6 md:p-10" data-testid="remesa-detail-page">
      <Link
        to="/remesas"
        data-testid="back-to-remesas"
        className="inline-flex items-center gap-1.5 text-sm text-med-muted hover:text-med-text mb-6"
      >
        <ArrowLeft size={14} weight="bold" />
        Volver a remesas
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-med-surface border border-med-border rounded-2xl p-6 md:p-8 mb-6"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-med-secondary/10 text-med-secondary grid place-items-center flex-shrink-0">
              <Bank size={24} weight="duotone" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-1">
                Remesa
              </div>
              <h1
                className="font-display text-3xl md:text-4xl font-bold text-med-text"
                data-testid="remesa-nombre"
              >
                {remesa.nombre}
              </h1>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${estadoStyle}`}
                  data-testid="remesa-estado"
                >
                  {REMESA_ESTADO_LABEL[estado]}
                </span>
                <span className="text-sm text-med-muted">
                  {formatDate(remesa.fecha)}
                </span>
              </div>
              {remesa.descripcion && (
                <p className="text-sm text-med-muted mt-2 max-w-xl">
                  {remesa.descripcion}
                </p>
              )}
            </div>
          </div>
          <div className="text-right md:text-right">
            <div className="text-xs uppercase tracking-[0.12em] text-med-muted">
              Total
            </div>
            <div
              className="font-display text-3xl font-bold text-med-primary"
              data-testid="remesa-total"
            >
              {formatEUR(remesa.total)}
            </div>
            <div className="text-sm text-med-muted">
              {remesa.num_recibos} recibo{remesa.num_recibos === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Banner readonly */}
        {!editable && (
          <div
            className="mt-6 rounded-xl border border-med-border bg-med-surface2 px-4 py-3 text-sm text-med-muted"
            data-testid="remesa-readonly-banner"
          >
            Esta remesa está en estado{" "}
            <span className="font-semibold">{REMESA_ESTADO_LABEL[estado]}</span>{" "}
            y no puede modificarse. Solo las remesas en{" "}
            <span className="font-mono">borrador</span> son editables.
          </div>
        )}

        {/* Acciones */}
        {canManage && (
          <div
            className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-med-border"
            data-testid="remesa-actions"
          >
            <button
              onClick={descargarSepa}
              data-testid="action-sepa"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-med-secondary hover:bg-med-secondary-hover text-white text-sm font-semibold transition-colors"
              title="Descargar SEPA pain.008"
            >
              <DownloadSimple size={16} weight="bold" />
              Descargar SEPA
            </button>
            <button
              onClick={descargarExcel}
              data-testid="action-excel"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-med-secondary hover:bg-med-secondary-hover text-white text-sm font-semibold transition-colors"
              title="Descargar Excel"
            >
              <DownloadSimple size={16} weight="bold" />
              Descargar Excel
            </button>

            {transicionesPosibles.includes("enviada") && (
              <button
                onClick={() => transicionar("enviada")}
                data-testid="action-marcar-enviada"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-med-border hover:border-sky-500 text-sky-700 text-sm font-semibold transition-colors"
              >
                <PaperPlaneTilt size={16} weight="duotone" />
                Marcar enviada
              </button>
            )}
            {transicionesPosibles.includes("cobrada") && (
              <button
                onClick={() => transicionar("cobrada")}
                data-testid="action-marcar-cobrada"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-med-border hover:border-emerald-500 text-emerald-700 text-sm font-semibold transition-colors"
              >
                <CheckCircle size={16} weight="duotone" />
                Marcar cobrada
              </button>
            )}
            {editable && (
              <button
                onClick={eliminar}
                data-testid="action-delete"
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl hover:bg-med-danger/10 text-med-danger text-sm font-semibold transition-colors"
              >
                <Trash size={16} weight="duotone" />
                Eliminar remesa
              </button>
            )}
          </div>
        )}
      </motion.div>

      {/* Listado de recibos */}
      <section className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
        <header className="px-6 py-4 border-b border-med-border">
          <h2 className="font-display text-lg font-bold text-med-text">
            Recibos incluidos
          </h2>
          <p className="text-sm text-med-muted">
            {remesa.num_recibos} recibos, total {formatEUR(remesa.total)}.
          </p>
        </header>
        <div className="divide-y divide-med-border">
          {groupedRecibos.length === 0 ? (
            <div className="px-5 py-12 text-center text-med-muted">
              No hay recibos vinculados a esta remesa.
            </div>
          ) : (
            groupedRecibos.map((group) => {
              const tieneRecargo = aplicaRecargoMesImpar(remesa.fecha, group);

              const totalGrupo = group.total + (tieneRecargo ? 1.2 : 0);

              return (
                <div key={group.group_key} className="p-5">
                  {/* HEADER GRUPO */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-lg font-bold text-med-text">
                          {group.titular || "Sin titular"}
                        </div>

                        {group.referencia && (
                          <span className="px-2 py-1 rounded-full bg-med-secondary/10 text-med-secondary text-xs font-semibold">
                            {group.referencia}
                          </span>
                        )}

                        {group.tipo === "pago_en_mano" && (
                          <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-semibold">
                            Pago en mano
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-med-muted mt-1">
                        {group.recibos.length} recibo
                        {group.recibos.length !== 1 ? "s" : ""}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs uppercase tracking-[0.12em] text-med-muted">
                        Total agrupado
                      </div>

                      <div className="font-display text-2xl font-bold text-med-primary">
                        {formatEUR(totalGrupo)}
                      </div>
                    </div>
                  </div>

                  {/* RECIBOS */}
                  <div className="overflow-hidden rounded-2xl border border-med-border">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
                          <th className="px-5 py-3">Nº</th>

                          <th className="px-5 py-3">Fallero</th>

                          <th className="px-5 py-3">Fecha pago</th>

                          <th className="px-5 py-3 text-right">Importe</th>

                          {editable ? (
                            <th className="px-5 py-3 text-right">Acciones</th>
                          ) : (
                            <th />
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {group.recibos.map((rec) => (
                          <tr
                            key={rec.id}
                            className="border-t border-med-border/60"
                          >
                            <td className="px-5 py-3 font-mono text-sm">
                              {rec.numero}
                            </td>

                            <td className="px-5 py-3 text-sm font-medium">
                              {rec.fallero_nombre}
                            </td>

                            <td className="px-5 py-3 text-sm text-med-muted">
                              {formatDate(rec.fecha_pago)}
                            </td>

                            <td className="px-5 py-3 text-right font-semibold">
                              {formatEUR(rec.importe)}
                            </td>

                            <td className="px-5 py-3 text-right">
                              {editable && rec.estado === "pendiente" && (
                                <div className="inline-flex gap-1">
                                  <button
                                    onClick={() => openEdit(rec)}
                                    className="p-2 rounded-lg hover:bg-med-surface2 text-med-secondary"
                                  >
                                    <PencilSimple size={16} weight="duotone" />
                                  </button>

                                  <button
                                    onClick={() => unlinkRecibo(rec)}
                                    className="p-2 rounded-lg hover:bg-yellow-50 text-yellow-700"
                                  >
                                    <LinkBreak size={16} weight="duotone" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {tieneRecargo && (
                    <div className="mt-3 flex justify-end">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold">
                        <span className="text-base">⚡</span>
                        Recargo mes impar
                        <span className="font-bold">+1,20 €</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
      {editModal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setEditModal(null)}
        >
          <motion.form
            onSubmit={saveEdit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between sticky top-0 bg-med-surface">
              <h3 className="font-display text-xl font-bold">Editar recibo</h3>
              <button
                type="button"
                onClick={() => setEditModal(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fallero <span className="text-med-primary">*</span>
                  </label>
                  <select
                    required
                    value={editModal.data.fallero_id}
                    onChange={(e) =>
                      setEditModal({
                        ...editModal,
                        data: {
                          ...editModal.data,
                          fallero_id: e.target.value,
                        },
                      })
                    }
                    data-testid="recibo-fallero"
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none disabled:opacity-60"
                  >
                    <option value="">— Selecciona —</option>
                    {falleros.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.nombre} {f.apellidos} · {f.categoria}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fecha de pago <span className="text-med-primary">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={editModal.data.fecha_pago}
                    onChange={(e) =>
                      setEditModal({
                        ...editModal,
                        data: {
                          ...editModal.data,
                          fecha_pago: e.target.value,
                        },
                      })
                    }
                    data-testid="recibo-fecha-pago"
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Estado
                  </label>
                  <select
                    value={editModal.data.estado}
                    onChange={(e) =>
                      setEditModal({
                        ...editModal,
                        data: { ...editModal.data, estado: e.target.value },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="pagado">Pagado</option>
                    <option value="anulado">Anulado</option>
                  </select>
                </div>
              </div>

              {/* Conceptos */}
              <div className="border-t border-med-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-display text-lg font-bold text-med-text">
                    Conceptos
                  </h4>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={addConcepto}
                      data-testid="add-concepto"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-med-secondary/10 text-med-secondary hover:bg-med-secondary/20"
                    >
                      <Plus size={14} weight="bold" />
                      Añadir concepto
                    </button>
                  </div>
                </div>

                {editModal.data.conceptos.length === 0 ? (
                  <div className="text-sm text-med-muted bg-med-surface2 rounded-xl p-4 text-center">
                    {editModal.data.fallero_id
                      ? "No hay conceptos. Pulsa 'Añadir concepto' para empezar."
                      : "Selecciona un fallero para autocargar conceptos por defecto."}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {editModal.data.conceptos.map((c, i) => (
                      <div
                        key={i}
                        data-testid={`concepto-row-${i}`}
                        className="grid grid-cols-12 gap-2 items-center bg-med-surface2/60 rounded-xl p-2"
                      >
                        <select
                          value={c.tipo}
                          onChange={(e) =>
                            updateConcepto(i, "tipo", e.target.value)
                          }
                          data-testid={`concepto-tipo-${i}`}
                          className="col-span-5 rounded-lg bg-white border border-med-border px-3 py-2 text-sm outline-none focus:border-med-primary"
                        >
                          {TIPOS.map((t) => (
                            <option key={t} value={t}>
                              {CONCEPTO_LABEL[t]}
                            </option>
                          ))}
                        </select>
                        <input
                          value={c.descripcion || ""}
                          onChange={(e) =>
                            updateConcepto(i, "descripcion", e.target.value)
                          }
                          placeholder="Descripción (opcional)"
                          className="col-span-4 rounded-lg bg-white border border-med-border px-3 py-2 text-sm outline-none focus:border-med-primary"
                        />
                        <div className="col-span-2 relative">
                          <input
                            type="number"
                            step="0.01"
                            value={c.importe}
                            onChange={(e) =>
                              updateConcepto(i, "importe", e.target.value)
                            }
                            data-testid={`concepto-importe-${i}`}
                            className="w-full rounded-lg bg-white border border-med-border px-3 py-2 text-sm text-right font-semibold outline-none focus:border-med-primary"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-med-muted pointer-events-none hidden">
                            €
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeConcepto(i)}
                          data-testid={`concepto-remove-${i}`}
                          className="col-span-1 p-2 rounded-lg text-med-danger hover:bg-med-danger/10"
                        >
                          <Trash size={16} weight="duotone" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-end items-center gap-3 pt-2 px-2">
                      <span className="text-xs uppercase tracking-[0.12em] text-med-muted font-semibold">
                        Total
                      </span>
                      <span
                        data-testid="recibo-total"
                        className="font-display text-2xl font-bold text-med-primary"
                      >
                        {formatEUR(totalConceptos(editModal.data.conceptos))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Notas
                </label>
                <textarea
                  rows={2}
                  value={editModal.data.notas || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      data: { ...editModal.data, notas: e.target.value },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3 sticky bottom-0 bg-med-surface">
              <button
                type="button"
                onClick={() => setEditModal(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                Guardar
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </div>
  );
}
